import { useState, useEffect } from 'react';
import { get } from '../../../api/client';
import { CHART_PALETTE } from '../../../utils/serviceTypeColor';

interface ServiceTypeStat {
  name: string;
  orderCount: number;
  totalAmount: number;
  paidAmount: number;
}

/** `GET /orders/statistics` 的 `serviceCount[]`：服务端已经按类型聚合完的三读数。 */
interface ServiceTypeBucket {
  serviceType: string | null;
  count: number;
  amount: number;
  paidAmount: number;
}

/** 订单一单没填服务类型时的落位，措辞与 app 端统计页的服务类型卡一致。 */
const UNCLASSIFIED = '未分类';

/**
 * 配色只按**行序**从 `CHART_PALETTE` 取，绝不按服务类型名称取——名字表在这张卡上失灵过一次
 * （四个写死的名字有三个系统里根本没有，真实类型全落同一支灰，两行读数只能靠文字区分）。
 * 这里和 chip 的「同名恒色」是两种取法：分布图要相邻行必然不同色，色号跟着排名走。
 */

function formatAmt(val: number): string {
  if (val >= 10000) return `¥${(val / 10000).toFixed(1)}万`;
  return `¥${val.toLocaleString('zh-CN')}`;
}

export default function ServiceTypeChart() {
  const [stats, setStats] = useState<ServiceTypeStat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await get<{ serviceCount?: ServiceTypeBucket[] }>('/orders/statistics');

        // 分组键取订单上落的服务类型名（服务端 groupBy `service_type`），**不取目录**：目录只反映
        // 「我这侧添加过哪些类型」，而经对方交互进来的订单带的名字可能压根不在我的目录里（自己没建过
        // 这个类型）。照目录取行会把这些单子整笔漏掉——实测本地库 admin 的「摄影服务」¥3,000 就是这么
        // 消失的，卡上合计 ¥1,889 对不上同页统计卡的收入总额。反过来，目录里一单没有的类型也不该占
        // 一行：这张卡回答的是「钱从哪些类型来」，不是「我的目录清单」。
        //
        // 过滤与求和都在服务端做（`activeMoneyWhere(userId,'income')`）：判据是「未软删 + 审核未拒 +
        // 未取消 + 视角收入向」，与这里原先逐条 if 的三条完全对应，但方向按登录方视角判定，比读
        // 已经翻转过的中文 `orderCategory` 字符串准。客户端不再需要把整张订单表拉回浏览器自己加。
        const byType = new Map<string, ServiceTypeStat>();
        for (const b of res?.serviceCount ?? []) {
          const rawName = (b.serviceType ?? '').trim();
          const name = rawName.length > 0 ? rawName : UNCLASSIFIED;
          const stat = byType.get(name) ?? { name, orderCount: 0, totalAmount: 0, paidAmount: 0 };
          stat.orderCount += Number(b.count) || 0;
          stat.totalAmount += Number(b.amount) || 0;
          stat.paidAmount += Number(b.paidAmount) || 0;
          byType.set(name, stat);
        }

        // 金额降序、单数作第二键：金额并列时顺序不能随服务端 groupBy 的返回顺序漂。
        // 这条排序与两端移动端的 `rankServiceTypes` 是同一算式，改动要三处一起看。
        setStats(
          [...byType.values()].sort(
            (a, b) => b.totalAmount - a.totalAmount || b.orderCount - a.orderCount
          )
        );
      } catch (error) {
        console.error('获取服务类型统计失败:', error);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const maxAmount = Math.max(...stats.map(s => s.totalAmount), 1);
  const grandTotal = stats.reduce((s, st) => s + st.totalAmount, 0);

  if (loading) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-5 animate-pulse">
        <div className="h-4 bg-gray-100 rounded w-32 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-20 h-3 bg-gray-100 rounded" />
              <div className="flex-1 h-5 bg-gray-100 rounded-full" />
              <div className="w-16 h-3 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasData = stats.some(s => s.totalAmount > 0);

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-teal-50">
            <i className="ri-pie-chart-2-line text-sm text-teal-600" />
          </div>
          <p className="text-sm font-semibold text-gray-700">服务类型收入分布</p>
        </div>
        {grandTotal > 0 && (
          <span className="text-xs text-gray-400">
            合计 <strong className="text-gray-600">{formatAmt(grandTotal)}</strong>
          </span>
        )}
      </div>

      {!hasData ? (
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full mb-2">
            <i className="ri-bar-chart-grouped-line text-xl" />
          </div>
          <p className="text-sm">暂无收入数据</p>
        </div>
      ) : (
        <div className="space-y-3.5">
          {stats.map((st, i) => {
            const color = CHART_PALETTE[i % CHART_PALETTE.length];
            const pct = grandTotal > 0 ? (st.totalAmount / grandTotal) * 100 : 0;
            const barWidth = maxAmount > 0 ? (st.totalAmount / maxAmount) * 100 : 0;
            const collectRate = st.totalAmount > 0 ? (st.paidAmount / st.totalAmount) * 100 : 0;

            return (
              <div key={st.name} className="group">
                {/* Top row: swatch+name | pct | amount */}
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={`w-3.5 h-3.5 rounded shrink-0 mx-[3px] ${color.bar}`} />
                  <span className={`text-xs font-medium ${color.text} w-20 shrink-0 truncate`} title={st.name}>{st.name}</span>
                  <div className="flex-1 relative h-5 bg-gray-100 rounded-full overflow-hidden">
                    {/* 总额条 */}
                    <div
                      className={`absolute left-0 top-0 h-full ${color.bar} rounded-full transition-all duration-700`}
                      style={{ width: `${barWidth}%` }}
                    />
                    {/* 已收款覆盖层 */}
                    {collectRate > 0 && (
                      <div
                        className={`absolute left-0 top-0 h-full opacity-30 ${color.bar} rounded-full transition-all duration-700`}
                        style={{ width: `${barWidth * (collectRate / 100)}%` }}
                      />
                    )}
                  </div>
                  <div className="text-right shrink-0 min-w-[80px]">
                    <span className="text-xs font-semibold text-gray-700">{formatAmt(st.totalAmount)}</span>
                    {pct > 0 && (
                      <span className="text-xs text-gray-400 ml-1">({pct.toFixed(0)}%)</span>
                    )}
                  </div>
                </div>

                {/* Sub info: order count + collection rate */}
                <div className="flex items-center gap-3 pl-7 text-[11px] text-gray-400">
                  <span>{st.orderCount} 单</span>
                  {st.totalAmount > 0 && (
                    <>
                      <span className="text-gray-200">|</span>
                      <span>
                        已收 <strong className="text-teal-500">{formatAmt(st.paidAmount)}</strong>
                      </span>
                      <span className="text-gray-200">|</span>
                      <span>
                        收款率 <strong className={collectRate >= 100 ? 'text-teal-500' : collectRate > 50 ? 'text-amber-500' : 'text-rose-400'}>
                          {collectRate.toFixed(0)}%
                        </strong>
                      </span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Legend: bar meaning */}
      {hasData && (
        <div className="mt-4 pt-3 border-t border-gray-50 flex items-center gap-4 text-[11px] text-gray-400">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-2 rounded-full bg-gray-300" />
            总收入额
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-2 rounded-full bg-teal-300 opacity-60" />
            已收款额
          </div>
        </div>
      )}
    </div>
  );
}
